import { Alert, Box, Stack, Typography } from "@mui/material";
import { useValidateCouponMutation } from "Api/Mutations/Coupons/useValidateCouponMutation";
import { InputActionButton } from "Components/Shared/Inputs/InputActionButton";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import type {
  LicenseCode,
  Region,
  ValidateCouponResult,
} from "Infrastructure/Api/Api";
import { isCouponError } from "Components/Licenses/CouponInput/couponErrors";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { useCallback, useState } from "react";

const RESOURCES = Resources.Components.LicenseOrder.Coupon;

type Props = {
  licenseCode: LicenseCode;
  region: Region;
  onCouponValidated: (code: string | null) => void;
};

export const CouponInput = ({
  licenseCode,
  region,
  onCouponValidated,
}: Props) => {
  const { t } = useResource();
  const [couponCode, setCouponCode] = useState("");
  const [validatedResult, setValidatedResult] =
    useState<ValidateCouponResult | null>(null);
  const [validatedCode, setValidatedCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: validateCoupon, isPending } = useValidateCouponMutation({
    onSuccess: (result) => {
      setValidatedResult(result);
      setValidatedCode(couponCode.trim().toUpperCase());
      setErrorMessage(null);
      onCouponValidated(couponCode.trim().toUpperCase());
    },
    onError: (error: unknown) => {
      setValidatedResult(null);
      setValidatedCode(null);
      const couponError = isCouponError(error);
      setErrorMessage(
        t(
          couponError
            ? RESOURCES.Error[couponError]
            : RESOURCES.Error.Unavailable,
        ),
      );
      onCouponValidated(null);
    },
  });

  const handleValidate = useCallback(() => {
    const code = couponCode.trim();

    if (!code) {
      return;
    }

    setErrorMessage(null);
    validateCoupon({ code, licenseCode, region });
  }, [couponCode, licenseCode, region, validateCoupon]);

  const handleRemove = useCallback(() => {
    setCouponCode("");
    setValidatedResult(null);
    setValidatedCode(null);
    setErrorMessage(null);
    onCouponValidated(null);
  }, [onCouponValidated]);

  const formatPrice = (amount: number) =>
    formatCurrency({ amount, currencyCode: validatedResult?.currencyCode });

  if (validatedResult && validatedCode) {
    return (
      <Stack gap={3}>
        <Alert severity="success" onClose={handleRemove}>
          <Typography fontWeight={600}>
            {t(RESOURCES.Applied)} — {validatedCode}
          </Typography>
        </Alert>
        <Stack gap={0.5} px={1}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(RESOURCES.OriginalPrice)}
            </Typography>
            <Typography variant="body2" sx={{ textDecoration: "line-through" }}>
              {formatPrice(validatedResult.originalPriceWithTax)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(RESOURCES.Discount)}
            </Typography>
            <Typography variant="body2" color="error.main">
              -{formatPrice(validatedResult.discountAmount)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>
              {t(RESOURCES.FinalPrice)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatPrice(validatedResult.finalPriceWithTax)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Box flex={1}>
        <TextInput
          name="couponCode"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          label={t(RESOURCES.Label)}
          placeholder={t(RESOURCES.Placeholder)}
          error={!!errorMessage}
          helperText={errorMessage || " "}
          disabled={isPending}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleValidate();
            }
          }}
        />
      </Box>
      <InputActionButton
        onClick={handleValidate}
        isDisabled={isPending}
        isPending={isPending}
      >
        {t(RESOURCES.Validate)}
      </InputActionButton>
    </Stack>
  );
};
